import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { orderBy } from 'lodash'

import { AnalyticsPerformance } from './utils/types'
import { AnalyticsField, DuckDBSchemaType, FilterField } from '@types'

import { ANALYTICS_CAPTIONS } from '../utils/const'
import { ASSET_FIELD } from '@/const'

import { DuckDBService } from '@/services/duckdb'

import { getCurrentDate } from '@/helpers/dates'
import { getFilterTags, getFilterValues } from '../utils/helpers'

import useAnalyticsStore from '../index'
import { useAssetsBunchStore } from '@/store/assets/bunch'
import { useTagsBunchStore } from '@/store/tags/bunch'

export const TABLE_NAME = 'analytics_performance'

const TABLE_SCHEMA: Record<string, DuckDBSchemaType> = {
  [AnalyticsField.DATE]: 'DATE',
  [AnalyticsField.ACCOUNT_ID]: 'VARCHAR',
  [AnalyticsField.ASSET_ID]: 'VARCHAR',
  tags: 'VARCHAR[]',
  [AnalyticsField.VALUE_START]: 'DOUBLE',
  [AnalyticsField.VALUE_END]: 'DOUBLE',
  [AnalyticsField.CHANGE]: 'DOUBLE',
  [AnalyticsField.PERCENT_CHANGE]: 'DOUBLE',
  [AnalyticsField.CAPITAL_FLOW]: 'DOUBLE',
  [AnalyticsField.REALIZED_GAIN]: 'DOUBLE',
  [AnalyticsField.UNREALIZED_GAIN]: 'DOUBLE',
}

export default defineStore('analytics-performance', () => {
  // INIT
  const duckdbInstance = DuckDBService.instance()

  const analyticsStore = useAnalyticsStore()

  const list = ref<Record<string, AnalyticsPerformance[]>>()

  const loading = ref(false)
  const deprecated = ref<boolean>()
  const trigger = ref(0)

  // GETTERS

  const isInit = computed(() => deprecated.value !== undefined)

  const getList = computed(() => {
    if (analyticsStore.getError) return {}
    return list.value
  })

  const getDatesRange = computed(() => {
    return list.value && Object.keys(list.value)
  })

  // SETTERS

  const setList = async (data: {
    performance_view: Record<string, AnalyticsPerformance[]>
  }) => {
    if (deprecated.value === false) return
    if (!data.performance_view) return
    list.value = data.performance_view
    deprecated.value = false
    await duckdbInstance.createTable(TABLE_NAME, TABLE_SCHEMA)
    await Promise.all(
      Object.entries(data.performance_view ?? {}).map(([date, performance]) =>
        duckdbInstance.pushData(
          TABLE_NAME,
          performance.map(item => ({ 
            ...item, 
            [AnalyticsField.DATE]: date
          })),
          TABLE_SCHEMA,
        ),
      ),
    )
    trigger.value++
  }

  // ACTIONS

  const clear = () => {
    list.value = undefined
    loading.value = false
    deprecated.value = undefined
    trigger.value = 0
  }

  return {
    isLoading: loading,
    isDeprecated: deprecated,

    isInit,
    trigger,

    getList,
    getDatesRange,

    setList,

    fetch,

    clear,
  }
})

export const useAnalyticsPerformanceFilters = () => {
  const analyticsStore = useAnalyticsStore()
  const assetsBunchStore = useAssetsBunchStore()
  const tagsBunchStore = useTagsBunchStore()

  const analyticsDate = computed(() => analyticsStore.getDate || getCurrentDate({}))

  const getValues = getFilterValues<AnalyticsPerformance>(TABLE_NAME, analyticsDate, assetsBunchStore)
  const getTags = getFilterTags(TABLE_NAME, analyticsDate, tagsBunchStore)

  const filters = computed(() => {
    const result: FilterField[] = [
      {
        name: AnalyticsField.ACCOUNT_ID,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.ACCOUNT_ID],
        getter: () => getValues(AnalyticsField.ACCOUNT_ID, ASSET_FIELD.NAME)
      },
      {
        name: AnalyticsField.ACCOUNT_TYPE,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.ACCOUNT_TYPE],
        getter: () => getValues(AnalyticsField.ACCOUNT_ID, ASSET_FIELD.TYPE)
      },
      {
        name: AnalyticsField.ASSET_ID,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.ASSET_ID],
        getter: () => getValues(AnalyticsField.ASSET_ID, ASSET_FIELD.NAME)
      },
      {
        name: AnalyticsField.ASSET_TYPE,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.ASSET_TYPE],
        getter: () => getValues(AnalyticsField.ASSET_ID, ASSET_FIELD.TYPE)
      },
    ]
    tagsBunchStore.getMetricTags?.forEach(name => {
      result.push({
        name,
        caption: name,
        getter: () => getTags(name)
      })
    })
    return orderBy(result, 'caption')
  })

  return {
    filters,
  }
}
